/**
 * POWERHOUSE-INGEST-01 — the review surface.
 *
 * SubInvoiceReviewRail sits at the top of the Jobs page whenever Powerhouse
 * invoices are waiting: each row shows what the parser READ (amount, permit,
 * ESC job, customer) and exactly WHERE Apply will write it. One click applies;
 * wrong read → office edits nothing here, they dismiss and enter manually
 * (the manual path stays forever). Forward-to-GM sends the original email —
 * one click replaces printing the invoice for the GM's desk.
 *
 * JobSubInvoices is the same ledger scoped to one job's Money tab.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap, FileText, Check, X, Send, Settings2, ShieldCheck, Loader2, ExternalLink,
  ChevronDown,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import {
  type SubInvoice, useSubInvoices, useJobSubInvoices, useApplySubInvoice,
  useDismissSubInvoice, useForwardSubInvoiceToGm, useGmSettings, useSaveGmSettings,
  subInvoicePdfUrl,
} from "./subinvoices";

const fmt$ = (n: number | null) => (n == null ? "—" : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: n % 1 === 0 ? 0 : 2 }));
const fmtDate = (s: string | null) => (s ? new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");

/** One invoice row — shared between the rail and the job tab. */
function InvoiceRow({ inv, compact }: { inv: SubInvoice; compact?: boolean }) {
  const apply = useApplySubInvoice();
  const dismiss = useDismissSubInvoice();
  const forward = useForwardSubInvoiceToGm();
  const [confirmDismiss, setConfirmDismiss] = useState(false);
  const [gmOpen, setGmOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const openPdf = async () => {
    if (!inv.pdf_storage_path) return;
    const url = await subInvoicePdfUrl(inv.pdf_storage_path);
    if (url) window.open(url, "_blank", "noopener");
  };
  const doForward = () =>
    forward.mutate(inv.id, {
      onError: (e) => {
        if (String(e.message).includes("no_gm_email")) setGmOpen(true);
        else setErr(String(e.message));
      },
    });

  const applied = inv.status === "applied";

  return (
    <li className={cn("px-4 py-3", applied && "bg-emerald-50/30")}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {/* What it is */}
        <span className="inline-flex items-center gap-1.5 text-sm">
          <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="font-semibold text-slate-800">#{inv.invoice_no ?? "?"}</span>
          <span className="text-xs text-slate-400">{fmtDate(inv.invoice_date)}</span>
        </span>
        <span className="text-sm font-bold tabular-nums text-slate-900">{fmt$(inv.amount)}</span>

        {/* Who it's for */}
        <span className="min-w-0 flex-1 truncate text-sm text-slate-600">
          <span className="font-medium text-slate-700">{inv.customer_name ?? "Unmatched — pick manually"}</span>
          {inv.city ? <span className="text-slate-400"> · {inv.city}</span> : null}
          {inv.esc_dispatch ? <span className="text-xs text-slate-400"> · ESC job {inv.esc_dispatch}</span> : null}
        </span>

        {/* The permit — the whole point */}
        {inv.permit_no && (
          <span className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
            applied ? "bg-emerald-100 text-emerald-800" : "bg-sky-50 text-sky-700",
          )}>
            <ShieldCheck className="h-3 w-3" /> Permit #{inv.permit_no}
          </span>
        )}

        {/* GM state */}
        {inv.gm_routed_at ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700" title={`Emailed to ${inv.gm_routed_to}`}>
            <Check className="h-3 w-3" /> GM {new Date(inv.gm_routed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        ) : (
          <button type="button" onClick={doForward} disabled={forward.isPending}
            title="Email the original invoice to the GM for payment"
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700">
            {forward.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Forward to GM
          </button>
        )}

        {/* Actions */}
        <span className="flex items-center gap-1.5">
          {inv.pdf_storage_path && (
            <button type="button" onClick={openPdf} title="Open the invoice PDF"
              className="rounded p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600">
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
          )}
          {applied ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
              <Check className="h-3.5 w-3.5" /> Applied
            </span>
          ) : (
            <>
              <button type="button" onClick={() => apply.mutate(inv)} disabled={apply.isPending}
                className="inline-flex items-center gap-1 rounded-full bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-800 disabled:opacity-50">
                {apply.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Apply
              </button>
              {confirmDismiss ? (
                <button type="button" onClick={() => dismiss.mutate(inv.id)}
                  className="rounded-full bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white">
                  Dismiss?
                </button>
              ) : (
                <button type="button" onClick={() => setConfirmDismiss(true)} title="Dismiss — enter it manually instead"
                  className="rounded p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
        </span>
      </div>

      {/* Where Apply will write — spelled out BEFORE the click. */}
      {!applied && !compact && (
        <p className="mt-1 pl-5 text-[11px] leading-snug text-slate-400">
          Apply files{inv.permit_no ? <> permit <span className="font-medium text-slate-500">#{inv.permit_no}</span> into the permit system</> : " no permit (none listed)"}
          {inv.matched_job_id ? <>, adds {fmt$(inv.amount)} as a subcontractor cost on <Link to={`/jobs/${inv.matched_job_id}`} className="font-medium text-brand-600 hover:underline">the install job</Link></> : null}
          {inv.pdf_storage_path && inv.custno ? ", and files the PDF on the customer's documents" : ""}.
          {!inv.matched_job_id && inv.custno ? " ESC-era job — no Pegasus job to cost against." : ""}
        </p>
      )}
      {err && <p className="mt-1 pl-5 text-[11px] text-rose-600">{err}</p>}
      {gmOpen && <GmSettingsModal onClose={() => setGmOpen(false)} />}
    </li>
  );
}

/** Jobs-page rail — appears only while something needs a decision. */
export function SubInvoiceReviewRail() {
  const { data: invoices } = useSubInvoices("review");
  const [gmOpen, setGmOpen] = useState(false);
  // Collapsed one-liner by default (Seth 2026-08-21: "don't want it missed
  // but it's taking up a lot of real estate") — the count + total stay
  // visible up top; the rows unfold on click.
  const [open, setOpen] = useState(false);
  if (!invoices?.length) return null;
  const total = invoices.reduce((s, i) => s + (i.amount ?? 0), 0);

  return (
    <Card className="mb-4 overflow-hidden border-violet-100">
      {/* DESC-CLEAN-01: the (i) can't live inside a <button>, so the toggle is a
          transparent button covering the whole strip; the (i) and the gear are
          positioned (relative) so they sit above it and take their own clicks. */}
      <div className={cn("relative flex w-full flex-wrap items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-violet-50/70", open ? "border-b border-violet-100 bg-violet-50/50" : "bg-violet-50/40")}>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          aria-label="Show or hide the Powerhouse invoices" className="absolute inset-0" />
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <Zap className="h-4 w-4" />
        </span>
        <div className="flex min-w-0 items-center gap-1.5 leading-tight">
          <h3 className="text-[13px] font-semibold text-slate-800">Powerhouse Invoices — Review &amp; Apply</h3>
          <InfoTip label="About Powerhouse invoices">Read automatically from their emails. Nothing is written until you click Apply.</InfoTip>
        </div>
        <span className="ml-auto rounded-full bg-white px-2.5 py-1 text-xs font-semibold tabular-nums text-violet-700 shadow-sm">
          {invoices.length} waiting · {fmt$(total)}
        </span>
        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setGmOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setGmOpen(true); } }}
          title="GM payment routing settings"
          className="relative rounded p-1.5 text-slate-300 transition-colors hover:bg-white hover:text-slate-600">
          <Settings2 className="h-4 w-4" />
        </span>
        {/* pointer-events-none: once rotated, the chevron paints above the toggle and would swallow its click. */}
        <ChevronDown className={cn("pointer-events-none h-4 w-4 text-violet-400 transition-transform", open && "rotate-180")} />
      </div>
      {open && (
        <ul className="divide-y divide-slate-100">
          {invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} />)}
        </ul>
      )}
      {gmOpen && <GmSettingsModal onClose={() => setGmOpen(false)} />}
    </Card>
  );
}

/** The same ledger scoped to one job — lives on Money & Close-out. */
export function JobSubInvoices({ jobId, noSubs, onToggleNoSubs, saving }: {
  jobId: string;
  /** SUBS-02 (Seth 2026-08-25): the office can mark "no subcontractor on this
   *  job" so an empty list reads as ANSWERED, not still-waiting. */
  noSubs?: boolean;
  onToggleNoSubs?: () => void;
  saving?: boolean;
}) {
  const { data: invoices } = useJobSubInvoices(jobId);
  const empty = !invoices?.length;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2">
        <Zap className="h-4 w-4 text-violet-500" />
        <h3 className="text-[13px] font-semibold text-slate-700">Subcontractor Invoices</h3>
        {empty && noSubs && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">
            <Check className="h-3 w-3" /> No Subcontractor
          </span>
        )}
      </div>
      {empty ? (
        noSubs ? (
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm text-slate-500">
            No subcontractor on this job — nothing to wait for here.
            {onToggleNoSubs && (
              <button type="button" disabled={saving} onClick={onToggleNoSubs}
                className="text-xs font-medium text-slate-400 underline-offset-2 transition-colors hover:text-slate-600 hover:underline">
                Undo
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
            <p className="text-sm text-slate-500">
              No subcontractor invoices yet — Powerhouse invoices match up here automatically when they arrive.
            </p>
            {onToggleNoSubs && (
              <button type="button" disabled={saving} onClick={onToggleNoSubs}
                className="shrink-0 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-200">
                No Subcontractor on This Job
              </button>
            )}
          </div>
        )
      ) : (
        <ul className="divide-y divide-slate-100">
          {invoices!.map((inv) => <InvoiceRow key={inv.id} inv={inv} compact />)}
        </ul>
      )}
    </Card>
  );
}

/** GM routing — email + optional auto-forward. Writes need a Pegasus admin. */
function GmSettingsModal({ onClose }: { onClose: () => void }) {
  const { data: gm } = useGmSettings();
  const save = useSaveGmSettings();
  const [email, setEmail] = useState(gm?.email ?? "");
  const [auto, setAuto] = useState(gm?.auto ?? false);
  const [seeded, setSeeded] = useState(false);
  if (gm && !seeded) { setEmail(gm.email); setAuto(gm.auto); setSeeded(true); }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
          GM Payment Routing
          <InfoTip label="About GM payment routing">Powerhouse invoices are emailed to this address for payment: the original message with the PDFs attached, so nothing needs printing.</InfoTip>
        </h3>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-slate-500">GM email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="gm@sedgwickheating.com"
          className="mt-1 w-full rounded-md border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        <div className="mt-3 flex items-center gap-2">
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="mt-0.5" />
            <span className="font-medium text-slate-700">Forward automatically</span>
          </label>
          <InfoTip label="What forwarding automatically does">Every new Powerhouse invoice email goes straight to the GM the moment Pegasus reads it.</InfoTip>
        </div>
        {save.isError && <p className="mt-2 text-xs text-rose-600">Couldn&apos;t save — this setting needs a Pegasus admin account.</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">Cancel</button>
          <button type="button" disabled={save.isPending || !email.trim()}
            onClick={() => save.mutate({ email, auto }, { onSuccess: onClose })}
            className="rounded-md bg-brand-700 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50">
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
